/**
 * SHA-256 in the browser.
 *
 * Hashing before upload is what makes duplicate detection free: a file we
 * already hold is refused after one small JSON request instead of after 25 MB
 * of transfer. On a job-site connection that is the difference between a
 * two-second answer and a two-minute one.
 *
 * WHY NOT STREAMING
 *   `crypto.subtle.digest` needs the whole buffer. Streaming SHA-256 means a
 *   userland implementation, another dependency, and a much slower one, since
 *   Web Crypto runs native code. At a 25 MB ceiling, holding one file in memory
 *   is fine; what is NOT fine is holding a hundred, which is why hashing is
 *   bounded by the same concurrency limit as uploading.
 *
 * The digest is verified server-side against the stored bytes by the
 * `verify_checksum` worker, so a client that lies is caught, it just is not
 * caught synchronously.
 */

export interface HashResult {
  readonly sha256: string;
  readonly byteSize: number;
  readonly durationMs: number;
}

/**
 * Hashes a file, reporting progress as it reads.
 *
 * Reading in chunks rather than one `file.arrayBuffer()` call keeps the UI
 * responsive and gives the progress bar something honest to show, on a large
 * scan, hashing is a visible fraction of the wait.
 */
export async function hashFile(
  file: Blob,
  options: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {},
): Promise<HashResult> {
  const startedAt = performance.now();
  const chunkSize = 4 * 1024 * 1024;
  const chunks: Uint8Array[] = [];
  let offset = 0;

  while (offset < file.size) {
    if (options.signal?.aborted === true) {
      throw new DOMException('Hashing was cancelled.', 'AbortError');
    }
    const end = Math.min(offset + chunkSize, file.size);
    chunks.push(new Uint8Array(await file.slice(offset, end).arrayBuffer()));
    offset = end;
    options.onProgress?.(file.size === 0 ? 1 : offset / file.size);
    // Yield to the event loop between chunks. Without this a 25 MB file blocks
    // paint long enough to look like a freeze.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  const combined = new Uint8Array(file.size);
  let written = 0;
  for (const chunk of chunks) {
    combined.set(chunk, written);
    written += chunk.byteLength;
  }

  const digest = await crypto.subtle.digest('SHA-256', combined);

  return {
    sha256: toHex(new Uint8Array(digest)),
    byteSize: file.size,
    durationMs: Math.round(performance.now() - startedAt),
  };
}

export function toHex(bytes: Uint8Array): string {
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Magic-byte sniffing.
 *
 * Browsers report MIME types inconsistently and sometimes not at all: Windows
 * sends `application/octet-stream` for anything it does not recognise, some
 * Android builds send an empty string, and a renamed `.jpg` will happily claim
 * to be a PDF. Reading the first bytes is the only source that cannot lie.
 *
 * Returns null when the signature is unrecognised, in which case the caller
 * falls back to the extension.
 */
export async function sniffMimeType(file: Blob): Promise<string | null> {
  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const startsWith = (...bytes: number[]): boolean =>
    bytes.every((byte, index) => header[index] === byte);

  // %PDF
  if (startsWith(0x25, 0x50, 0x44, 0x46)) return 'application/pdf';
  if (startsWith(0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  // RIFF....WEBP
  if (startsWith(0x52, 0x49, 0x46, 0x46) && header[8] === 0x57 && header[9] === 0x45) {
    return 'image/webp';
  }
  // TIFF, little- and big-endian
  if (startsWith(0x49, 0x49, 0x2a, 0x00) || startsWith(0x4d, 0x4d, 0x00, 0x2a)) {
    return 'image/tiff';
  }
  // ISO-BMFF: ....ftypheic / ftypheix / ftypmif1
  if (header[4] === 0x66 && header[5] === 0x74 && header[6] === 0x79 && header[7] === 0x70) {
    const brand = new TextDecoder().decode(header.slice(8, 12));
    if (brand.startsWith('hei') || brand.startsWith('mif')) return 'image/heic';
  }
  // XLSX is a zip. PK\003\004 alone cannot distinguish it from any other zip,
  // so the extension decides, which is why xlsx is store-only.
  if (startsWith(0x50, 0x4b, 0x03, 0x04)) return null;

  return null;
}
